import { h, type VNode } from 'vue';
import { message } from 'hpsfir-managebase-ui';
import { Copy, RefreshCw } from 'lucide-vue-next';

/**
 * 「二维码图片」（小程序码）与「NFC 链接」两个单元格的公共实现。
 *
 * 抽出来的原因：二维码管理页和驿站管理页都要展示这两列（驿站专属码也要有），
 * 复制逻辑与样式只应有一份 —— 否则两个页面的复制行为/换行样式会慢慢长歪。
 *
 * 样式在 `src/style/qr-url-cell.scss`（全局，跟着 global.scss 一起加载）：
 * render 返回的 VNode 由 ProTable（父组件）渲染，写在页面里的 `<style scoped>`
 * 对它无效 —— 这个坑踩过，所以公共样式只能放全局。
 */

/** 相对资源路径补成绝对地址（qrImageUrl 是 /upload/xxx.png 这种静态路径） */
export function absoluteUrl(base: string, url?: string) {
  const value = String(url || '');
  if (!value) {
    return '';
  }
  return /^https?:/u.test(value) ? value : `${base}${value}`;
}

/**
 * 复制文本到剪贴板。
 *
 * navigator.clipboard 只在安全上下文可用（https / localhost）；管理后台可能部署在
 * 内网 http 上，那时它是 undefined 而不是报错，所以必须有降级：
 * 用一个不可见的 textarea + document.execCommand('copy')。
 */
export async function copyText(text: string) {
  if (!text) {
    return;
  }
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      message.success('已复制链接');
      return;
    }
  } catch {
    // 落到下面的降级分支
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', 'readonly');
  ta.style.position = 'fixed';
  ta.style.top = '-1000px';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (ok) {
    message.success('已复制链接');
  } else {
    message.error('复制失败，请手动选择文本复制');
  }
}

/** 「二维码图片」单元格：小程序码缩略图，没有图片时给一个占位文案 */
export function renderQrImageCell(scope: any, base: string, size = 72) {
  const src = absoluteUrl(base, scope?.row?.qrImageUrl);
  if (!src) {
    return '—';
  }
  const style = `width:${size}px;height:${size}px;display:block;border:1px solid var(--border,#e6e8eb);`
    + 'border-radius:6px;padding:4px;background:#fff;';
  return h('img', { src, style });
}

/**
 * 「NFC 链接」单元格：链接 + 一键复制按钮。
 *
 * 空值分两种情况，文案必须区分开 —— 都显示空白的话，运营会分不清
 * 「还没申请到 NFC 能力」和「这枚码生成失败了」：
 *   - nfcReady=false：NFC 设备参数还没配，属于「待配置设备」；
 *   - nfcReady=true 但链接为空：该码需要点「重新生成」补一次 NFC 链接。
 *
 * 内层再包一层 div：主题对「单元格内容层的直接子元素」会强制 display:block
 * （.ultra-table-cell-content--width-limited > *），flex 放在外层 div 上会被覆盖，
 * 所以 flex 布局必须落在自己的内层包裹上。
 */
export interface UrlSchemeCellOptions {

  /** 点「重新生成」时调用（单个重新生成 NFC 链接）。不传则不显示该按钮 */
  onRefresh?: (row: any) => void;
}

export function renderUrlSchemeCell(scope: any, options: UrlSchemeCellOptions = {}): VNode | string {
  const row = scope?.row || {};
  const url = row.urlScheme;
  const { onRefresh } = options;

  const refreshBtn = onRefresh
    ? h(
      'button',
      {
        'class': 'qr-url-refresh',
        'type': 'button',
        'title': '重新生成 NFC 链接',
        'aria-label': '重新生成 NFC 链接',
        'onClick': (evt: MouseEvent) => {
          evt.stopPropagation();
          onRefresh(row);
        },
      },
      [h(RefreshCw, { 'class': 'qr-url-refresh-icon' })],
    )
    : null;

  if (!url) {
    // 空值分两种，文案必须区分（否则运营分不清「还没申请到能力」和「这枚生成失败」）：
    //   nfcReady=false → 设备参数还没配，等审核；此时「重新生成」点了也只会被拒，但仍保留按钮，
    //   点下去会给出明确原因，比一个没有解释的灰按钮好排查。
    const placeholder = row.nfcReady === false ? '待配置设备' : '—';
    return h(
      'div',
      { 'class': 'qr-url-cell' },
      [h('div', { 'class': 'qr-url-inner' }, [h('span', { 'class': 'qr-url-empty' }, placeholder), refreshBtn])],
    );
  }

  const onCopy = (evt: MouseEvent) => {
    evt.stopPropagation();
    copyText(String(url));
  };
  const copyBtn = h(
    'button',
    {
      'class': 'qr-url-copy',
      'type': 'button',
      'title': '复制链接',
      'aria-label': '复制链接',
      'onClick': onCopy,
    },
    [h(Copy, { 'class': 'qr-url-copy-icon' })],
  );
  const text = h('span', { 'class': 'qr-url-text' }, String(url));
  return h('div', { 'class': 'qr-url-cell' }, [h('div', { 'class': 'qr-url-inner' }, [text, copyBtn, refreshBtn])]);
}


/**
 * 「选择」单元格：批量操作用的行选择框。
 *
 * 为什么不用框架的 `selection` / `batchOperates`（踩过，框架 bug）：
 *   HpsfirProTable 给 HpsfirCheckbox 传的是 `:checked` + `@update:checked`，
 *   而 HpsfirCheckbox 声明的是 `modelValue` / `update:modelValue` ——
 *   两者对不上：点击后 reka 内部 `data-state` 会变，但 `update:checked` 永不派发，
 *   框架的 `selects` 一直是空数组 → 「已选中 N 项」和批量入口永远不出现
 *   （手动 emit('update:checked') 能让它立刻出现，反证链路断在组件契约上）。
 *   框架在 node_modules 里，改了会被重装覆盖，所以在页面上自管选择。
 */
export function renderSelectCell(
  scope: any,
  isSelected: (row: any) => boolean,
  onToggle: (row: any, checked: boolean) => void,
) {
  const row = scope?.row || {};
  const checked = isSelected(row);
  const box = h('input', {
    'type': 'checkbox',
    'class': 'qr-select-box',
    checked,
    'aria-label': '选择该行',
    'onClick': (evt: MouseEvent) => evt.stopPropagation(),
    'onChange': (evt: Event) => onToggle(row, Boolean((evt.target as HTMLInputElement).checked)),
  });
  return h('div', { 'class': 'qr-select-cell' }, [box]);
}
